import { useEffect, useState } from 'react'
import { ADMIN_EMAIL, CONTACT_MAILTO } from '../lib/config'
import { checkDatabaseReady } from '../lib/waitForBackend'

const BACKEND = import.meta.env.VITE_API_URL ?? ''

// Same cadence as the boot gate's unavailable screen (AppBootstrap).
const RECHECK_MS = 30_000

type Message = {
  heading: string
  body: string
  showContact: boolean
  backLabel?: string
  backHref?: string
}

const MESSAGES: Record<string, Message> = {
  no_invite: {
    heading: 'Invitation required',
    body: 'Proof of Work is invitation-only. Your Google account is not associated with a valid invitation.',
    showContact: true,
  },
  suspended: {
    heading: 'Account suspended',
    body: 'Your account has been suspended.',
    showContact: true,
  },
  oauth_failed: {
    heading: 'Sign-in failed',
    body: 'Something went wrong during sign-in. Please try again.',
    showContact: false,
  },
  service_unavailable: {
    heading: 'Temporarily unavailable',
    body: 'Sign-in is unavailable right now. Your records are safe — please try again later.',
    showContact: false,
    // Signing in is exactly what just failed, so don't offer it as the way back.
    backLabel: 'Back to homepage',
  },
}

// Shown in place of service_unavailable once the service answers again, so a
// tab left open doesn't keep saying sign-in is down after it's back.
const RECOVERED_MESSAGE: Message = {
  heading: 'Sign-in is available again',
  body: 'The service is back. You can sign in now.',
  showContact: false,
  backLabel: 'Sign in',
  backHref: `${BACKEND}/auth/google`,
}

const DEFAULT_MESSAGE: Message = {
  heading: 'Sign-in failed',
  body: 'Something went wrong. Please try again.',
  showContact: false,
}

/**
 * Rendered pre-gate (main.tsx), outside any Router: the page that explains the
 * service is unavailable must not itself wait on the service. Reads the query
 * string directly for that reason — it also renders in-app after a client-side
 * navigate(), where window.location is already current.
 */
export default function AuthErrorPage() {
  const reason = new URLSearchParams(window.location.search).get('reason') ?? ''
  const [recovered, setRecovered] = useState(false)

  useEffect(() => {
    if (reason !== 'service_unavailable') return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const recheck = async () => {
      const state = await checkDatabaseReady(1)
      if (cancelled) return
      if (state === 'ready') setRecovered(true)
      else timer = setTimeout(recheck, RECHECK_MS)
    }
    timer = setTimeout(recheck, RECHECK_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [reason])

  const {
    heading,
    body,
    showContact,
    backLabel = 'Back to sign in',
    backHref = '/',
  } = recovered ? RECOVERED_MESSAGE : (MESSAGES[reason] ?? DEFAULT_MESSAGE)

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-stone-900">{heading}</h1>
            <p className="text-stone-500 text-sm leading-relaxed">{body}</p>
            {showContact && (
              <p className="text-stone-500 text-sm leading-relaxed">
                Email{' '}
                <a
                  href={CONTACT_MAILTO}
                  className="font-medium text-stone-700 underline hover:text-stone-900 transition-colors"
                >
                  {ADMIN_EMAIL}
                </a>{' '}
                to request access.
              </p>
            )}
          </div>

          <div className="flex flex-col items-center gap-3">
            {showContact && (
              <a
                href={CONTACT_MAILTO}
                className="inline-block px-4 py-2 bg-stone-800 text-white text-sm font-medium rounded-lg hover:bg-stone-700 transition-colors"
              >
                Contact admin
              </a>
            )}
            <a
              href={backHref}
              className={
                showContact
                  ? 'text-stone-500 text-sm hover:text-stone-700 transition-colors'
                  : 'inline-block px-4 py-2 bg-stone-800 text-white text-sm font-medium rounded-lg hover:bg-stone-700 transition-colors'
              }
            >
              {backLabel}
            </a>
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-xs text-stone-400">
        <a href="/privacy" className="hover:text-stone-600 transition-colors">
          Privacy
        </a>
      </footer>
    </div>
  )
}
